import { Component, Input, OnInit, AfterViewInit, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs/Rx';

import { HtmlService } from './../../../../../../services/html.service';
import * as fromRoot from './../../../../../../store/reducers';
import * as Enum from '../../../../../../dtos/enums';
import { ResponseService } from '../../../../../../services/response.service';
import { TestContentService } from '../../../../../../services/test-content.service';
import { ScoringPageService } from '../../../../../../services/scoring-page.service';
import { TestNavigationService } from '../../../../../../services/test-navigation.service';
import { PreviewService } from '../../../../../../services/preview.service';

interface ScoreSummary {
    Components: ScoreSummaryComponent[];
}
interface ScoreSummaryComponent {
    ComponentTypeId: number;
    ComponentName: string;
    TotalQuestions: number;
    TotalScore: number;
    PercentScore: number;
    BandName: string;
    BandDescription: string;
}

@Component({
    selector: 'ipp-feedback-page',
    template: require('./ipp-feedback-page.component.html'),
    changeDetection: ChangeDetectionStrategy.Default
})
export class IppFeedbackPageComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() title: string;
    @Input() disclaimerResourceId: number;
    @Input() readyResourceId: number;
    @Input() almostReadyResourceId: number;
    @Input() notReadyResourceId: number;

    testlets = [];
    interactions = [];
    scoreSummary: ScoreSummary = { Components: []};
    disclaimerText: string = "";
    bandReady: boolean = true;

    private assessmentTestSubscription: Subscription;
    private assessmentTest: PgRendererDto.AssessmentTest;
    private testNavigationSubscription: Subscription;
    private testNavigation:  PgRendererDto.TestNavigation;

    constructor(
        private store: Store<fromRoot.State>,
        private responseService: ResponseService,
        private testContentService: TestContentService,
        private scoringPageService: ScoringPageService,
        private previewService: PreviewService,
        private testNavigationService: TestNavigationService,
        private htmlService: HtmlService,
        private route: ActivatedRoute
    ) {
        this.testNavigationSubscription = store.select(fromRoot.getTestNavigation).subscribe(testNavigation => {
            this.testNavigation = testNavigation;
            this.feedbackPageUpdate(this.testNavigation, this.assessmentTest);
        })

        this.assessmentTestSubscription = store.select(fromRoot.getAssessmentTest).subscribe(assessmentTest => {
            this.assessmentTest = assessmentTest;
            this.feedbackPageUpdate(this.testNavigation, this.assessmentTest);
        });
    }

    feedbackPageUpdate(testNavigation: PgRendererDto.TestNavigation, assessmentTest: PgRendererDto.AssessmentTest) {
        if(testNavigation && assessmentTest) {
            let testPartIndex = testNavigation.testPartIndex;
            this.testlets = this.testNavigationService.getTestletsByTestPart(assessmentTest.TestParts[testPartIndex], testPartIndex);
        }
    }

    ngOnInit() {
        this.bandReady = false;
        this.getDisclaimerText();
    }

    ngAfterViewInit() {
        this.interactions = [];
        this.setupScoreSummaries(Enum.ComponentTypeEnum.Listening, "Listening");
        this.setupScoreSummaries(Enum.ComponentTypeEnum.Reading, "Reading");
        // fetch the scores for the entire test (split into test parts)
        let sectionScoreObservable: Observable<void>[] = [];
        for(let i = 1; i < this.assessmentTest.TestParts.length-2; i++) {
            // the first test part is the landing page, the last two test parts are the scoring/feedback pages and end of test page
            let sectionTestlets = this.testNavigationService.getTestletsByTestPart(this.assessmentTest.TestParts[i], i);
            sectionScoreObservable.push(this.getSectionScores(sectionTestlets, i));
        }
        Observable.zip(...sectionScoreObservable).subscribe(value => {
            this.scoreSummary.Components.forEach(scoreSummaryComponent => {
                scoreSummaryComponent.PercentScore = scoreSummaryComponent.TotalScore * 100 / scoreSummaryComponent.TotalQuestions;
                if (isNaN(scoreSummaryComponent.PercentScore) || scoreSummaryComponent.PercentScore < 50) {
                    scoreSummaryComponent.BandName = "Not Ready";
                } else if (scoreSummaryComponent.PercentScore < 67 ) {
                    scoreSummaryComponent.BandName = "Almost Ready";
                } else {
                    scoreSummaryComponent.BandName = "Ready";
                }
                this.setBandDescription(scoreSummaryComponent);
            });
        });
    }

    ngOnDestroy() {
        if (this.testNavigationSubscription) {
            this.testNavigationSubscription.unsubscribe();
        }
        if (this.assessmentTestSubscription) {
            this.assessmentTestSubscription.unsubscribe();
        }
    }

    getSectionScores(sectionTestlets, testPartIndex): Observable<void> {
        let testletIds = sectionTestlets.map(s => s.testletId);
        return this.testContentService
            .getTestlets(testletIds, this.assessmentTest.Identifier)
            .map(testletDefs => this.processTestlets(testletDefs))
            .mergeMap(responses => this.testContentService.getPreviewResponseScores(responses))
            .map(responseScores => this.processResponseScores(responseScores));
    }

    private getInteractionsFromInteractionDefinitions(testletDefs: PgRendererDto.TestletDefinition[]): PgRendererDto.InteractionDefinition[] {
        // get all interactions belonging to the testlets in this test part
        var interactions: PgRendererDto.InteractionDefinition[] = [];
        testletDefs.forEach(t => {
            let testletInteractions = this.testContentService.getReferencedInteractionsByTestlet(t);
            if (testletInteractions.length >= 1) {
                if (interactions.length >= 1) {
                    interactions = interactions.concat(testletInteractions);
                } else interactions = testletInteractions;
            }
        });
        return interactions;
    }

    private setupScoreSummaries(componentTypeId: number, componentTypeName: string) {
        let scoreSummaryComponent: ScoreSummaryComponent = {
            ComponentTypeId: componentTypeId,
            ComponentName: componentTypeName, 
            TotalQuestions: 0,
            TotalScore: 0,
            PercentScore: 0,
            BandName: "",
            BandDescription: ""
        }
        this.scoreSummary.Components.push(scoreSummaryComponent);
        return scoreSummaryComponent;
    }

    private processTestlets(testletDefs: PgRendererDto.TestletDefinition[]): PgRendererDto.InteractionResponse[] {
        // Grab Summary for Components        
        let listeningSummaryComponent = this.scoreSummary.Components.find(c => c.ComponentTypeId == Enum.ComponentTypeEnum.Listening);
        let readingSummaryComponent = this.scoreSummary.Components.find(c => c.ComponentTypeId == Enum.ComponentTypeEnum.Reading);

        // Grab Interactions
        const interactionsInPart = this.getInteractionsFromInteractionDefinitions(testletDefs);
        this.interactions = this.interactions.concat(interactionsInPart);

        // Grab number of Questions
        interactionsInPart.forEach((i: PgRendererDto.InteractionDefinition) => {
            if (i.ComponentTypeId == 1) {
                listeningSummaryComponent.TotalQuestions++;
            } else if (i.ComponentTypeId == 2) {
                readingSummaryComponent.TotalQuestions++;
            }
        });

        // Grab all Responses in this Test Part
        return this.responseService.responses.filter(x => interactionsInPart.findIndex(i => i.InteractionId == x.InteractionId) >= 0);
    }

    private processResponseScores(responseScores: PgRendererDto.ResponseScoresDto) {
        let listeningSummaryComponent = this.scoreSummary.Components.find(c => c.ComponentTypeId == Enum.ComponentTypeEnum.Listening);
        let readingSummaryComponent = this.scoreSummary.Components.find(c => c.ComponentTypeId == Enum.ComponentTypeEnum.Reading);
        responseScores.ResponseScores.forEach(s => {
            const interaction = this.interactions.find((i: PgRendererDto.InteractionDefinition) =>  i.InteractionId === s.InteractionId);
            if (interaction && interaction.ComponentTypeId == 1) {
                listeningSummaryComponent.TotalScore += s.Score;
            } else if (interaction && interaction.ComponentTypeId == 2) {
                readingSummaryComponent.TotalScore += s.Score;
            }
        })
        return;
    }
    
    private setBandDescription(scoreSummaryComponent: ScoreSummaryComponent) {
        switch(scoreSummaryComponent.BandName) {
            case "Ready":
                this.htmlService.getHtmlResource(this.readyResourceId).subscribe(html => {
                    scoreSummaryComponent.BandDescription = html;
                });
                break;
            case "Almost Ready":
                this.htmlService.getHtmlResource(this.almostReadyResourceId).subscribe(html => {
                    scoreSummaryComponent.BandDescription = html;
                });
                break;
            case "Not Ready":
                this.htmlService.getHtmlResource(this.notReadyResourceId).subscribe(html => {
                    scoreSummaryComponent.BandDescription = html;
                });
                break;
            default:
                break;
        }
        this.bandReady = true;
    }

    getDisclaimerText() {
        this.htmlService.getHtmlResource(this.disclaimerResourceId).subscribe(html => {
            this.disclaimerText = html;
        });
    }
}





// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/components/pg-renderer/item-body/item-body-panel/ipp/ipp-feedback-page/ipp-feedback-page.component.ts